import { isNil } from 'lodash';
import type {
  IClient,
  Treatment as SplitTreatment,
  TreatmentWithConfig as SplitTreatmentWithConfig,
} from '@splitsoftware/splitio/types/splitio';
import { TreatmentWithConfig, Treatment } from '@surfline/web-common';

import type { UserState } from 'types/user';
import { SPLIT_INCOGNITO_ID } from 'common/constants';

export type Treatments = {
  [featureName: string]: SplitTreatment | SplitTreatmentWithConfig;
};

/**
 * Type guard for checking if a given treatment is of the type TreatmentWithConfig
 */
export const isTreatmentWithConfig = (
  treatment: Treatment | TreatmentWithConfig,
): treatment is TreatmentWithConfig =>
  !!treatment && Object.prototype.hasOwnProperty.call(treatment as TreatmentWithConfig, 'config');

/**
 * Method to get multiple Split Treatment statuses server-side
 */
export const getTreatments = (
  splitClient: IClient,
  treatments: string[],
  userState: UserState | null,
  anonymousId: string = SPLIT_INCOGNITO_ID,
) => {
  const splitId = userState?.details?._id ?? anonymousId ?? SPLIT_INCOGNITO_ID;
  const identify = {
    // Core attributes
    id: splitId,
    anonymousId,

    // Legacy attribute format for backwards compatibility
    userId: userState?.details?._id ?? '',
    entitlements: userState?.entitlements ?? '',
    email: userState?.details?.email ?? '',
    isoCountryCode: userState?.countryCode ?? '',
    subdivisionCode: userState?.subdivisionCode ?? '',

    // New attribute format (shared_*)
    shared_loggedIn: !!userState?.details?._id,
    shared_platform: 'web',
    ...(userState?.details?.email && { shared_email: userState.details.email }),
    ...(userState?.entitlements && { shared_entitlements: userState.entitlements }),
    ...(userState?.countryCode && { shared_isoCountryCode: userState.countryCode }),
    ...(userState?.subdivisionCode && { shared_subdivisionCode: userState.subdivisionCode }),
    ...(userState?.details?._id && { shared_userId: userState.details._id }),
    shared_isEmailVerified: userState?.details?.isEmailVerified ?? false,
    ...(userState?.ltaEntitlement && { shared_ltaEntitlement: userState.ltaEntitlement }),
  };
  if (isNil(splitClient)) {
    console.warn(
      'Warning: There is no Split Client created. Be sure the env var is defined for the Split.io API Key.',
    );
    return {};
  }
  if (isNil(treatments) || treatments?.length === 0) {
    console.warn(
      'Warning: Treatments need to be specified to enable Split.io. Split.io is not enabled.',
    );
    return {};
  }
  const treatmentsWithConfig = splitClient.getTreatmentsWithConfig(splitId, treatments, identify);
  const treatmentStatus: { [key: string]: Treatment | TreatmentWithConfig } = {};
  Object.entries(treatmentsWithConfig).forEach(([treatment, status]) => {
    treatmentStatus[treatment] = status.config ? status : status.treatment;
  });
  return treatmentStatus;
};

export const parseTreatmentWithConfig = (treatment: Treatment | TreatmentWithConfig) => {
  if (
    isTreatmentWithConfig(treatment) &&
    (treatment.treatment === 'on' ||
      treatment.treatment === 'on_premium_plus' ||
      treatment.treatment === 'on_no_banner') &&
    treatment.config
  )
    try {
      return JSON.parse(treatment.config);
    } catch (error) {
      console.log(error);
    }
  return null;
};
